import { useEffect, useRef, useState } from 'react';
import { usePipelineFlush } from '../hooks/usePipelineFlush';
import { MutationState } from '../../../request/interfaces/entity-ui-state';
import { Toc } from '../../../toc/Toc';
import { TocContentContainer } from '../../../toc/components/TocContentContainer';
import { TocSection } from '../../../toc/components/TocSection/TocSection';
import { PIPELINE_REVIEW_SECTIONS } from '../constants/pipeline-review-sections';
import { PipelineReviewSectionTitle } from './PipelineReviewSectionTitle';
import { Acknowledgment } from './Acknowledgment';
import type { PipelineReviewProps } from '../PipelineReview';
import { EntityDisplayConfig } from '../../../shared/interfaces/entity-display-config';
import EntityBanner from '../../../shared/components/EntityBanner/EntityBanner';
import { IntegrationBanner } from '../../../shared/interfaces/integration';
import styles from './PipelineReviewContent.module.scss';

export interface PipelineReviewContentProps extends PipelineReviewProps {
  content: {
    sourceDisplayConfig: EntityDisplayConfig;
    destinationDisplayConfig: EntityDisplayConfig;
    integrationDisplayConfig: EntityDisplayConfig;
    objectsDisplayConfig: EntityDisplayConfig;
    banners: IntegrationBanner[];
  };
}

export function PipelineReviewContent({
  title,
  subtitle,
  pipeline,
  content,
  onStateUpdate,
  isEditing,
  controller
}: PipelineReviewContentProps) {
  const { submitSaveRequest, mutationState } = usePipelineFlush(pipeline.id, !!isEditing, () =>
    controller!.getWizardController().markAsComplete()
  );
  const walSlotAckNeeded = useRef(pipeline.source!.sourceType.properties!.walSlotMonitoringEnabled);
  const [ackProvided, setAckProvided] = useState(false);

  useEffect(() => {
    onStateUpdate({
      showProgress: mutationState === MutationState.MUTATING,
      disabled:
        mutationState === MutationState.MUTATING || (walSlotAckNeeded.current && !ackProvided),
      onClick: () => submitSaveRequest()
    });
  }, [mutationState, ackProvided]);

  return (
    <Toc>
      <TocContentContainer title={title} subtitle={subtitle}>
        {PIPELINE_REVIEW_SECTIONS.map(
          ({
            dataAccessKey,
            id,
            title: sectionTitle,
            listItemTitle,
            componentProps,
            Component
          }) => {
            const config = content[dataAccessKey] as EntityDisplayConfig;
            const { hasDiff } = config;

            const className =
              hasDiff && componentProps?.showDiffMode ? 'w-85' : componentProps?.className;

            return (
              <TocSection
                key={id}
                domId={id}
                Title={PipelineReviewSectionTitle}
                titleProps={{ title: sectionTitle!, hasDiff }}
                listItemTitle={listItemTitle}
              >
                <Component {...componentProps} className={className} displayConfig={config} />
              </TocSection>
            );
          }
        )}

        {content.banners.length ? (
          <div className='my-6'>
            {content.banners.map(banner => (
              <EntityBanner
                key={banner.title}
                dataId={banner.title}
                {...banner}
                docLink={banner.docLink ?? undefined}
                className={styles.banner}
              />
            ))}
          </div>
        ) : null}

        {walSlotAckNeeded.current ? (
          <TocSection key='acknowledgment' domId='acknowledgment' listItemTitle='Acknowledgment'>
            <Acknowledgment defaultChecked={false} onAckChange={setAckProvided} />
          </TocSection>
        ) : null}
      </TocContentContainer>
    </Toc>
  );
}
